"use client";

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import ClientHref from "@/components/ui/client-href";
import Title from "@/components/ui/title";
import { useCurrentLocale } from "@/hooks/useCurrentLocale";
import { cn } from "@/lib/utils";
import { useScopedI18n } from "@/messages/client";
import { Fragment, useMemo } from "react";

export default function ContactOffice(props: {
  data?: {
    headOffice?: {
      title?: string;
      switchboard?: string;
      business?: string;
      mailAddress?: string;
    };
    branchOffices?: {
      name?: string;
      offices?: {
        name?: string;
        level?: string;
        address?: string;
        phone?: string;
      }[];
    }[];
  };
}) {
  const locale = useCurrentLocale();

  const t = useScopedI18n("联系我们");

  const branchOffices = useMemo(() => {
    if (!props.data?.branchOffices || props.data.branchOffices.length < 2) {
      return props.data?.branchOffices;
    }

    const [first, second] = props.data.branchOffices;
    const mergedOffices =
      locale === "en"
        ? [...(second.offices || []), ...(first.offices || [])]
        : [...(first.offices || []), ...(second.offices || [])];

    return [
      {
        ...first,
        offices: mergedOffices,
      },
    ];
  }, [locale, props.data?.branchOffices]);

  return (
    <div className="w-full">
      <Title title={t("业务咨询与服务")} underline={false} />
      <div className="mt-5 rounded-2xl bg-[url(/contact-bg.png)] bg-cover bg-center pb-6 pl-8 pr-12 pt-9 shadow-md md:mt-14 md:py-11 md:pl-14 md:pr-8">
        <div
          className={cn(
            "relative text-base font-bold md:text-2xl",
            "before:absolute before:-left-3 before:top-1/2 before:h-4 before:-translate-y-1/2 before:border-l-2 before:border-primary md:before:-left-5 md:before:h-6 md:before:border-l-4"
          )}>
          {props.data?.headOffice?.title ?? t("上海总部联系方式")}
        </div>
        <div className="mt-3 flex flex-col gap-y-5 md:mt-8 md:flex-row">
          <div className="w-full md:w-1/2">
            <div className="text-base md:text-lg">{t("服务热线")}</div>
            <div className="mt-3 flex flex-col gap-y-3 md:flex-row md:flex-wrap md:justify-between">
              {props.data?.headOffice?.switchboard && (
                <ClientHref
                  href={`tel:${props.data.headOffice.switchboard}`}
                  className="flex items-start text-sm text-zinc-500 md:text-base">
                  <img
                    className="mr-1 w-5 md:mt-0.5"
                    src="/icon/phone.svg"
                    alt="Icon Phone"
                  />
                  {t("总机服务")}
                  {props.data?.headOffice?.switchboard}
                </ClientHref>
              )}
              {props.data?.headOffice?.business && (
                <ClientHref
                  href={`tel:${props.data.headOffice.business}`}
                  className="flex items-start text-sm text-zinc-500 md:text-base">
                  <img
                    className="mr-1 w-5 md:mt-0.5"
                    src="/icon/phone.svg"
                    alt="Icon Phone"
                  />
                  {t("业务咨询")}
                  {props.data?.headOffice?.business}
                </ClientHref>
              )}
            </div>
          </div>
          {props.data?.headOffice?.mailAddress && (
            <>
              <hr className="mx-16 mt-2.5 h-10 border-l-2 max-md:hidden" />
              <div className="w-full md:w-1/2">
                <div className="text-base md:text-lg">{t("邮寄地址")}</div>
                {props.data?.headOffice?.mailAddress && (
                  <div className="mt-3 flex items-start text-sm text-zinc-500 md:text-base">
                    <img
                      className="mr-3 w-5 md:mt-0.5"
                      src="/icon/location.svg"
                      alt="Icon Location"
                    />

                    {props.data.headOffice.mailAddress}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
      <div className="mt-10 flex flex-col gap-y-10 md:mt-[108px]">
        {branchOffices?.map((el, index) => (
          <Fragment key={index}>
            {el.name && (
              <h3 className="text-center text-xl font-bold md:text-left md:text-4xl md:leading-normal">
                {el.name}
              </h3>
            )}

            <div className="mt-12 flex flex-wrap justify-between gap-y-12 max-md:hidden">
              {el.offices?.map((item, i) => (
                <div
                  key={`office_${index}_${i}`}
                  className="w-full rounded-xl bg-zinc-100 px-16 py-10 md:w-[47%]">
                  <div className="flex items-center">
                    <img
                      className="mb-1 w-6"
                      src="/icon/location2.svg"
                      alt="Icon Location"
                    />
                    <span className="ml-2.5 text-xl font-bold">
                      {item.name}
                    </span>
                    {item.level && (
                      <span className="ml-3 text-base leading-7">
                        ({item.level})
                      </span>
                    )}
                  </div>
                  <div className="mt-5 text-base text-zinc-500">
                    {item.address}
                  </div>
                  <ClientHref
                    href={`tel:${item.phone}`}
                    className="mt-1.5 inline-block text-base text-zinc-500">
                    {t("联系电话")}
                    {item.phone}
                  </ClientHref>
                </div>
              ))}
            </div>
            <Accordion
              type="single"
              collapsible
              className="mt-5 flex flex-col gap-y-3.5 md:hidden">
              {el.offices?.map((item, i) => (
                <AccordionItem
                  className="overflow-hidden rounded-lg border-none"
                  key={`office_${index}_${i}`}
                  value={`office_${index}_${i}`}>
                  <AccordionTrigger className="flex items-center bg-zinc-100 px-5">
                    <div className="flex items-center">
                      <img
                        className="w-[18px]"
                        src="/icon/location2.svg"
                        alt="Icon Location"
                      />
                      <span className="ml-1.5 text-base font-bold">
                        {item.name}
                      </span>
                      {item.level && (
                        <span className="ml-1 text-sm leading-6">
                          ({item.level})
                        </span>
                      )}
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="bg-zinc-100 px-5 pb-4 pt-2">
                    <div className="text-sm text-zinc-500">{item.address}</div>
                    <ClientHref
                      href={`tel:${item.phone}`}
                      className="mt-1.5 inline-block text-sm text-zinc-500">
                      {t("联系电话")}
                      {item.phone}
                    </ClientHref>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Fragment>
        ))}
      </div>
    </div>
  );
}
